import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { utils, writeFile } from 'xlsx';
import * as fns from 'date-fns';
import { CustomModal } from '../../CustomModal';
import SalesReportHeader from './SalesReportHeader';
import { SalesReportTable } from './SalesReportTable';
import Lang from '../../../service/lang';

const zeroPad2 = (value: number) => (value < 10 ? `0${value}` : `${value}`);

interface SourceUnitReport {
    isExtra: boolean;
    names: string[];
    sales: [number, number]; // cash / card
}

export interface SalesReportResponse {
    source: string;
    resolution: string;
    startDate: Date | string;
    endDate: Date | string;
    intervals: Array<Date | string>;
    values: Record<number, SourceUnitReport>;
}

export interface SalesReportModalProps {
    data: SalesReportResponse;
    onClose: () => void;
}

export function SalesReportModal({ data, onClose }: SalesReportModalProps) {
    const [open, setOpen] = useState(true);
    const [filter, setFilter] = useState({
        cash: true,
        card: true,
        total: true,
    });
    const { t } = useTranslation();

    const onCloseHandler = useCallback(() => {
        setOpen(false);
        setTimeout(onClose, 500);
    }, [onClose]);

    const onToggleFilter = useCallback(
        (valueType: 'cash' | 'card' | 'total') => {
            setFilter((prev) => {
                if (
                    prev[valueType] &&
                    [prev.cash, prev.card, prev.total].filter(Boolean).length === 1
                ) {
                    return prev;
                }

                return {
                    ...prev,
                    [valueType]: !prev[valueType],
                };
            });
        },
        [],
    );

    const getFileRows = useCallback(() => {
        const values = Array.isArray(data.values)
            ? data.values.map((value, idx) => ({ id: idx, ...value }))
            : Object.keys(data.values).map((id) => ({
                  id: Number(id),
                  ...data.values[id],
              }));

        const rows = values.map((value) => {
            const sales = value.sales.map(([cash, card]) => ({
                cash,
                card,
                total: cash + card,
            }));
            const total = sales.reduce(
                (prev, crt) => ({
                    cash: prev.cash + crt.cash,
                    card: prev.card + crt.card,
                    total: prev.total + crt.total,
                }),
                { cash: 0, card: 0, total: 0 },
            );

            return {
                name: (value.names[0] ?? '?') + (value.isExtra ? ' *' : ''),
                sales,
                total,
            };
        });

        const totalsSales = data.intervals.map(() => ({
            cash: 0,
            card: 0,
            total: 0,
        }));
        const totals = rows.reduce(
            (prev, row) => {
                row.sales.forEach((item, idx) => {
                    totalsSales[idx].cash += item.cash;
                    totalsSales[idx].card += item.card;
                    totalsSales[idx].total += item.total;
                });
                prev.cash += row.total.cash;
                prev.card += row.total.card;
                prev.total += row.total.total;
                return prev;
            },
            { cash: 0, card: 0, total: 0 },
        );

        const intervalLabels = data.intervals.map((interval) => {
            const d = new Date(interval);
            if (data.resolution == 'hour') {
                return fns.format(d, 'HH:mm', {
                    locale: Lang.instance.getCurrentLocale(),
                });
            }
            if (data.resolution == 'day') {
                return `${zeroPad2(d.getDate())}/${zeroPad2(
                    d.getMonth() + 1,
                )}`;
            }
            if (data.resolution == 'month') {
                return fns.format(d, 'MMMM', {
                    locale: Lang.instance.getCurrentLocale(),
                });
            }
            return d.toLocaleString();
        });

        return [
            [
                t('name'),
                ...intervalLabels.map((label) => `${label}`),
                t('total'),
            ],
            ...rows.map((row) => [
                row.name,
                ...row.sales.map((s) => {
                    const selectedValues: number[] = [];
                    if (filter.cash) selectedValues.push(s.cash);
                    if (filter.card) selectedValues.push(s.card);
                    if (filter.total) selectedValues.push(s.total);

                    return selectedValues.length > 1
                        ? selectedValues.join('|')
                        : selectedValues[0];
                }),
                (() => {
                    const selectedValues: number[] = [];
                    if (filter.cash) selectedValues.push(row.total.cash);
                    if (filter.card) selectedValues.push(row.total.card);
                    if (filter.total) selectedValues.push(row.total.total);

                    return selectedValues.length > 1
                        ? selectedValues.join('|')
                        : selectedValues[0];
                })(),
            ]),
            [
                `${t('total')} (${rows.length})`,
                ...totalsSales.map((s) => {
                    const selectedValues: number[] = [];
                    if (filter.cash) selectedValues.push(s.cash);
                    if (filter.card) selectedValues.push(s.card);
                    if (filter.total) selectedValues.push(s.total);

                    return selectedValues.length > 1
                        ? selectedValues.join('|')
                        : selectedValues[0];
                }),
                (() => {
                    const selectedValues: number[] = [];
                    if (filter.cash) selectedValues.push(totals.cash);
                    if (filter.card) selectedValues.push(totals.card);
                    if (filter.total) selectedValues.push(totals.total);

                    return selectedValues.length > 1
                        ? selectedValues.join('|')
                        : selectedValues[0];
                })(),
            ],
        ];
    }, [data, t, filter]);

    const getFileName = useCallback(() => {
        const sd = new Date(data.startDate);
        const ed = new Date(data.endDate);

        const sourceName = data.source
                .replace(/\s+/g, '_')
                .replace(/[^a-zA-Z0-9_\-\.]/g, ''),
            startDate = `${sd.getFullYear()}_${zeroPad2(sd.getMonth() + 1)}_${zeroPad2(sd.getDate())}`,
            endDate = `${ed.getFullYear()}_${zeroPad2(ed.getMonth() + 1)}_${zeroPad2(ed.getDate())}`;

        return `${sourceName}_${t('products')}_${startDate}-${endDate}`;
    }, [t, data.startDate, data.endDate, data.source]);

    const exportToExcel = useCallback(() => {
        const ws = utils.aoa_to_sheet(getFileRows());
        const wb = utils.book_new();

        utils.book_append_sheet(wb, ws, 'Sheet1');
        writeFile(wb, `${getFileName()}.xlsx`);
    }, [getFileRows, getFileName]);

    const exportToCSV = useCallback(() => {
        const ws = utils.aoa_to_sheet(getFileRows());
        const wb = utils.book_new();

        utils.book_append_sheet(wb, ws, 'Sheet1');
        const csv = utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${getFileName()}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }, [getFileRows, getFileName]);

    return (
        <CustomModal
            open={open}
            onClose={onCloseHandler}
            title={data.source}
            header={
                <SalesReportHeader
                    onExportExcel={exportToExcel}
                    onExportCSV={exportToCSV}
                    filter={filter}
                    onToggleFilter={onToggleFilter}
                />
            }
            dialogStyle={{
                width: '100vw',
                maxWidth: 'none',
                paddingLeft: 10,
                paddingRight: 10,
            }}
            bodyStyle={{ height: '85vh' }}
        >
            <SalesReportTable
                data={data}
                filter={filter}
            />
        </CustomModal>
    );
}
